import { useState, useCallback, useMemo, MouseEvent } from 'react';

import { Popover } from '@mui/material';
import pluralize from 'pluralize';
import { useLocation } from 'react-router-dom';

import {
  DsShadow,
  Icons,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListSearchInput,
  ListSubheader,
  Scroller,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { OrganizationAvatar } from '@cast/shared-organization-ui';
import { OrganizationType, Organization } from '@cast/types';

import { useAbility } from '@cast/shared-ability-data-access';
import {
  getEnterpriseIdFromOrg,
  getEnterpriseSubPath,
  buildEnterpriseUrl,
  useNavigate,
} from '@cast/infrastructure-organization';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

type ViewByOrganizationSelectorProps = {
  viewedOrganization: Organization | undefined;
  onViewedOrganizationChange: (org: Organization) => void;
};

export const ViewByOrganizationSelector = ({
  viewedOrganization,
  onViewedOrganizationChange,
}: ViewByOrganizationSelectorProps) => {
  const { organizations = [], setCurrentOrganization } = useOrganizations();
  const canViewEnterprise = useAbility().can('view', 'EnterpriseManagement');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const isPopoverOpen = Boolean(anchorEl);

  // Derive enterprise ID from the viewed organization (single source of truth)
  const viewedEnterpriseId = useMemo(
    () => getEnterpriseIdFromOrg(viewedOrganization),
    [viewedOrganization]
  );

  // Get enterprise org if user has access
  const enterpriseOrg = useMemo(() => {
    if (!viewedEnterpriseId || !canViewEnterprise) return undefined;
    return organizations.find((o) => o.id === viewedEnterpriseId);
  }, [viewedEnterpriseId, canViewEnterprise, organizations]);

  // Get child orgs under this enterprise
  const childOrgs = useMemo(
    () =>
      organizations.filter(
        (org) =>
          org.type === OrganizationType.CHILD &&
          org.parentId === viewedEnterpriseId
      ),
    [organizations, viewedEnterpriseId]
  );

  // Count child orgs for enterprise
  const childOrgCount = childOrgs.length;

  // Filter orgs based on search
  const filteredChildOrgs = useMemo(() => {
    if (!searchQuery) return childOrgs;
    return childOrgs.filter((org) =>
      org.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [childOrgs, searchQuery]);

  // Define all hooks before any early returns
  const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const closePopover = useCallback(() => {
    setAnchorEl(null);
    setSearchQuery('');
  }, []);

  const handleSelectOrganization = useCallback(
    (orgId: string) => {
      const selectedOrg = organizations.find((o) => o.id === orgId);

      if (!selectedOrg) return;

      if (selectedOrg.type === OrganizationType.CHILD && viewedEnterpriseId) {
        // Switch to a different child org while staying on the same page
        const subPath =
          getEnterpriseSubPath(pathname, viewedEnterpriseId) || 'dashboard';
        setCurrentOrganization(orgId, { skipRedirect: true });
        navigate(buildEnterpriseUrl(viewedEnterpriseId, subPath, orgId));
      } else if (viewedEnterpriseId) {
        // For enterprise org, navigate to overview
        navigate(buildEnterpriseUrl(viewedEnterpriseId, 'overview'));
      }

      onViewedOrganizationChange(selectedOrg);
      closePopover();
    },
    [
      organizations,
      viewedEnterpriseId,
      pathname,
      navigate,
      closePopover,
      onViewedOrganizationChange,
      setCurrentOrganization,
    ]
  );

  // Early return if viewedOrganization is not loaded yet
  if (!viewedOrganization) {
    return null;
  }

  // Count total orgs user can view
  const totalViewableOrgs = (enterpriseOrg ? 1 : 0) + childOrgs.length;

  // Hide if only 1 org (check AFTER all hooks)
  if (totalViewableOrgs <= 1) {
    return null;
  }

  return (
    <Box mb={8}>
      <Stack gap={8}>
        <Typography variant="L10B" color={tokens.theme.fg.structural.secondary}>
          View by
        </Typography>

        <Box
          alignItems="center"
          borderRadius="4px"
          bgcolor="#fff"
          height="34px"
          padding="5px 8px 5px 6px"
          onClick={handleOpen}
          display="flex"
          justifyContent="space-between"
          sx={(theme) => ({
            border: `1px solid ${theme.palette.grey[200]}`,
            ...(isPopoverOpen && {
              border: `1px solid ${theme.palette.blue[500]}`,
              boxShadow: theme.shadows[DsShadow.ACTIVE_GLOW],
            }),
            '&:hover': {
              cursor: 'pointer',
            },
          })}
          data-testid="view-by-organization-selector-trigger"
        >
          {viewedOrganization && (
            <Stack
              direction="row"
              gap="8px"
              width="calc(100% - 16px)"
              alignItems="center"
            >
              <OrganizationAvatar
                organizationId={viewedOrganization.id}
                size="2xs"
              />
              <Stack overflow="hidden" justifyContent="center">
                <Typography variant="P14M" noWrap color="grey.700">
                  {viewedOrganization.name}
                </Typography>
              </Stack>
            </Stack>
          )}

          {isPopoverOpen ? (
            <Icons.CaretUp size={16} />
          ) : (
            <Icons.CaretDown size={16} />
          )}
        </Box>
      </Stack>
      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          marginTop: '4px',
          padding: '4px 0px',
        }}
        disableScrollLock
      >
        <Box width={anchorEl?.offsetWidth}>
          <ListSearchInput
            autoFocus
            autoComplete="off"
            size="small"
            placeholder="Search organizations"
            listItemProps={{
              sx: { '&.Mui-focusVisible': { background: 'none' } },
            }}
            value={searchQuery}
            onChange={(_, v) => setSearchQuery(v)}
            showClearButton
            clearButtonProps={{
              onClick: () => setSearchQuery(''),
            }}
          />

          <List size="medium" disablePadding testId="view-by-organization-list">
            <Scroller sx={{ gap: '2px', overflow: 'auto', maxHeight: '260px' }}>
              {/* Enterprise section */}
              {enterpriseOrg && (
                <>
                  <ListSubheader>ENTERPRISE</ListSubheader>
                  <ListItem
                    selected={viewedOrganization?.id === enterpriseOrg.id}
                    onClick={() => handleSelectOrganization(enterpriseOrg.id)}
                    startAdornment={
                      <ListItemAvatar>
                        <OrganizationAvatar
                          size="sm"
                          organizationId={enterpriseOrg.id}
                        />
                      </ListItemAvatar>
                    }
                    multiline
                  >
                    <ListItemText
                      primary={enterpriseOrg.name}
                      primaryTypographyProps={{ variant: 'P12M' }}
                      secondary={`${childOrgCount} ${pluralize(
                        'Child org',
                        childOrgCount
                      )}`}
                      secondaryTypographyProps={{ variant: 'P10R' }}
                    />
                  </ListItem>
                </>
              )}

              {/* Child organizations section */}
              <ListSubheader>CHILD ORGANIZATIONS</ListSubheader>
              {filteredChildOrgs.length === 0 ? (
                <ListItem multiline>
                  <ListItemText
                    primary="No organizations found"
                    primaryTypographyProps={{ variant: 'P12M' }}
                    secondary="Try adjusting the filter"
                    secondaryTypographyProps={{ variant: 'P10R' }}
                  />
                </ListItem>
              ) : (
                filteredChildOrgs.map((childOrg) => {
                  return (
                    <ListItem
                      key={childOrg.id}
                      selected={viewedOrganization?.id === childOrg.id}
                      onClick={() => handleSelectOrganization(childOrg.id)}
                      startAdornment={
                        <ListItemAvatar>
                          <OrganizationAvatar
                            size="sm"
                            organizationId={childOrg.id}
                          />
                        </ListItemAvatar>
                      }
                      multiline
                    >
                      <ListItemText
                        primary={childOrg.name}
                        primaryTypographyProps={{ variant: 'P12M' }}
                        secondary="Child Org"
                        secondaryTypographyProps={{ variant: 'P10R' }}
                      />
                    </ListItem>
                  );
                })
              )}
            </Scroller>
          </List>
        </Box>
      </Popover>
    </Box>
  );
};
